import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { GermanyMap } from '../components/GermanyMap';
import { PieChartIcon, BarChart3, MapPin, Loader2 } from 'lucide-react';
import api from '../lib/api';

const STATUS_LABELS = {
  unprocessed: 'Unbearbeitet',
  lead_generated: 'Lead generiert',
  contacted: 'Kontaktiert',
  no_interest: 'Kein Interesse',
  follow_up: 'Wiedervorlage',
};

const PIE_COLORS = ['#94a3b8', '#6366f1', '#10b981', '#ef4444', '#f59e0b', '#8b5cf6'];
const BAR_COLOR = '#6366f1';

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg shadow-lg px-3 py-2 text-sm">
      <p className="font-medium">{label || payload[0]?.name}</p>
      <p className="text-muted-foreground">{payload[0]?.value} Leads</p>
    </div>
  );
};

const PieLegend = ({ data }) => (
  <div className="flex flex-col gap-1.5 text-sm">
    {data.map((entry, i) => (
      <div key={entry.name} className="flex items-center gap-2">
        <div className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
        <span className="text-muted-foreground truncate">{STATUS_LABELS[entry.name] || entry.name}</span>
        <span className="font-semibold ml-auto">{entry.value}</span>
      </div>
    ))}
  </div>
);

export default function DashboardCharts() {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCharts = async () => {
      try {
        const res = await api.get('/discovery/dashboard-charts');
        setChartData(res.data);
      } catch (err) {
        console.error('Chart data load error:', err);
      }
      setLoading(false);
    };
    fetchCharts();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="charts-loading">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!chartData) return null;

  const { status_distribution, industry_distribution, bundesland_distribution, city_points } = chartData;
  const hasStatusData = status_distribution?.length > 0;
  const hasIndustryData = industry_distribution?.length > 0;
  const hasCityPoints = city_points?.length > 0;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8" data-testid="dashboard-charts">
      {/* Pie Chart — Lead Status */}
      <Card data-testid="chart-status-pie">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <PieChartIcon className="w-4 h-4 text-indigo-500" />
            Lead-Status Verteilung
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasStatusData ? (
            <div className="flex items-center gap-6">
              <div className="w-1/2 h-[200px]">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={status_distribution}
                      cx="50%"
                      cy="50%"
                      innerRadius={45}
                      outerRadius={85}
                      paddingAngle={2}
                      dataKey="value"
                      stroke="none"
                    >
                      {status_distribution.map((entry, i) => (
                        <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="w-1/2">
                <PieLegend data={status_distribution} />
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">Noch keine Daten vorhanden</p>
          )}
        </CardContent>
      </Card>

      {/* Germany Map */}
      <Card data-testid="chart-germany-map">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <MapPin className="w-4 h-4 text-indigo-500" />
            Aktivität nach Region
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">Punkte zeigen Städte mit bearbeiteten Leads</p>
        </CardHeader>
        <CardContent>
          {hasCityPoints ? (
            <div className="flex items-center justify-center">
              <GermanyMap cityPoints={city_points} bundeslandData={bundesland_distribution} className="h-[220px] w-auto" />
            </div>
          ) : (
            <div className="text-center py-8">
              <MapPin className="w-8 h-8 text-muted-foreground/30 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">Vergeben Sie einen Status bei einem Lead,<br/>um ihn auf der Karte zu sehen</p>
            </div>
          )}
          {hasCityPoints && (
            <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t justify-center">
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" /> Kontaktiert
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="w-2.5 h-2.5 rounded-full bg-indigo-500" /> Lead generiert
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="w-2.5 h-2.5 rounded-full bg-amber-500" /> Wiedervorlage
              </div>
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <span className="w-2.5 h-2.5 rounded-full bg-red-500" /> Kein Interesse
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Bar Chart — Industries */}
      <Card className="lg:col-span-2" data-testid="chart-industry-bar">
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-semibold flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-indigo-500" />
            Leads nach Branche
          </CardTitle>
        </CardHeader>
        <CardContent>
          {hasIndustryData ? (
            <div className="h-[220px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={industry_distribution} layout="vertical" margin={{ left: 10, right: 20, top: 5, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
                  <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12, fill: '#94a3b8' }} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    width={160}
                    tick={{ fontSize: 12, fill: '#64748b' }}
                    tickFormatter={(v) => v.length > 22 ? v.substring(0, 20) + '…' : v}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f1f5f9' }} />
                  <Bar dataKey="value" fill={BAR_COLOR} radius={[0, 4, 4, 0]} barSize={20} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">Noch keine Branchendaten vorhanden</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
